import { Suspense } from 'react';

import {
  Outlet,
  createFileRoute,
  notFound,
  redirect,
  retainSearchParams,
} from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';

import { PageSkeleton } from 'components/templates/PageSkeleton';
import { OpportunityError } from 'pages/Opportunity/components/OpportunityError';
import { oppCouponSearchSchema } from 'router/search/opportunity';
import { flagQueryOptions } from 'providers/FlagsProvider';
import { getOppSummaryQueryOptions } from 'services/partnersSelfSignup/opportunity';
import { rumService } from 'services/rum';

import { OpportunityChatbot } from './-components/OpportunityChatbot';

export const Route = createFileRoute('/opportunity/$oppId')({
  validateSearch: zodValidator(oppCouponSearchSchema),
  search: {
    // Deletion happens in oppCouponSearchSchema (STRIP_COUPON -> undefined),
    // not in a stripSearchParams middleware: a middleware would consume the
    // sentinel before the redirect's href is re-built, letting this retain
    // resurrect the deleted code and loop (ADR 012).
    middlewares: [retainSearchParams(['ssu_coupon'])],
  },
  component: () => (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <OpportunityChatbot />
      </Suspense>
    </>
  ),
  beforeLoad: async ({ context, params, matches }) => {
    const { queryClient, flags } = context;

    const shouldRedirect = matches.at(-1)?.routeId === Route.id;

    // Reads no hash, so it holds no credential: the flags below and every gate after this one start from it.
    const summary = await queryClient.query(
      getOppSummaryQueryOptions({ oppId: params.oppId }),
    );

    if (summary.oppExternalSource) {
      const externalSourcesFeature = {
        name: 'partners.self_signup.external_sources',
        fallbackValue: false,
      };

      const contextFlags = await queryClient.ensureQueryData(
        flagQueryOptions(
          {
            ...externalSourcesFeature,
            context: {
              sessionId: summary.oppExternalSource,
            },
          },
          {
            staleTime: 0,
          },
        ),
      );

      if (!contextFlags?.[externalSourcesFeature.name]) {
        rumService.addWarning('opportunity_not_found', {
          oppId: params.oppId,
          routeId: Route.id,
          gate: 'external_sources_flag_disabled',
          oppExternalSource: summary.oppExternalSource,
        });
        throw notFound();
      }
    }

    if (!summary.oppExternalSource && !flags.terms_and_conditions?.isEnabled) {
      rumService.addWarning('opportunity_not_found', {
        oppId: params.oppId,
        routeId: Route.id,
        gate: 'tc_flag_disabled_no_external_source',
      });
      throw notFound();
    }

    if (shouldRedirect) {
      throw redirect({
        to: `/opportunity/$oppId/terms-and-conditions`,
        params: { oppId: params.oppId },
      });
    }
  },
  pendingComponent: PageSkeleton,
  errorComponent: OpportunityError,
});
